/** @format */

import { Injectable } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { ToastService } from "mis-crystal-design-system/toast";
import { parseZone, tz, Moment } from "moment-timezone";
import { combineLatest, Observable, of, Subject, throwError } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { processShifts, tagsBasedOnStatus } from "../functions/booking-utils.functions";
import { NsWISConfigurations } from "../models/configurations.model";
import { NsEmpWebExpData } from "../models/emp-web-exp.model";
import { ApiService } from "./api.service";
import { ConfigurationService } from "./configuration.service";

const Minute = 60 * 1000;
const Hour = 60 * Minute;

@Injectable({
  providedIn: "root"
})
export class BookingDataService {
  heroBooking: NsEmpWebExpData.IBookingDetailDTOS | undefined;
  upcomingBookings: Array<NsEmpWebExpData.IList>;
  meetingBookings: Array<NsEmpWebExpData.IBookingDetailDTOS>;
  timezone: string;
  offices: Observable<NsEmpWebExpData.IOffice[]>;
  formConfiguration: NsEmpWebExpData.IBookingFormConfig = {
    mode: "CREATE",
    data: undefined,
    userConfigs: undefined,
    profile: undefined,
    heading: "Create Booking",
    type: "OFFICE"
  };
  preferences: NsEmpWebExpData.IPreference;
  private createBookings$ = new Subject<NsEmpWebExpData.IBookingRequest[]>();
  createBookings = this.createBookings$.asObservable();
  private updateBookings$ = new Subject<NsEmpWebExpData.IBookingRequest[]>();
  updateBookings = this.updateBookings$.asObservable();
  private cancelBookings$ = new Subject<NsEmpWebExpData.IBookingRequest[]>();
  cancelBookings = this.cancelBookings$.asObservable();
  private bookingActions$ = new Subject<NsEmpWebExpData.IBookingRequest[]>();
  bookingActions = this.bookingActions$.asObservable();
  profileRules: Observable<NsEmpWebExpData.IProfileRules>;
  activeBookings: NsEmpWebExpData.IList[];
  private bookingStartDate: number;
  private bookingEndDate: number;
  public readonly interval = 7;
  wfoExpiryTime: Moment;

  createResp: NsEmpWebExpData.IBookingRequest;
  createRecurrenceResp: NsEmpWebExpData.IRecurrenceBookingResponse;
  constructor(private apiService: ApiService, private configSvc: ConfigurationService, private toasts: ToastService) {}

  refreshReplayStreams(): void {
    this.offices = this.apiService.genReplayStream(this.apiService.getOffices());
    this.profileRules = this.apiService.genReplayStream(this.apiService.getProfileRules());
  }

  organiseBookings(bookings: NsEmpWebExpData.IBookingDetailDTOS[], approvalEnabled?: boolean): NsEmpWebExpData.IList[] {
    return bookings
      .sort((a, b) => a.startTime - b.startTime)
      .map(b => {
        const nextDay = parseZone(b.startTime).format("DD") !== parseZone(b.endTime).format("DD");
        b.extras && (b.extras.isNextDayLogout = nextDay);
        b.timezone = b.timezone || this.timezone;
        return {
          bookingRequestType: b.bookingRequestType || "MEETING",
          time: {
            startTime: b.startTime,
            endTime: b.endTime,
            timezone: b.timezone
          },
          tokenName: b?.extras?.subject,
          approvalEnabled: approvalEnabled,
          approvalText: tagsBasedOnStatus(b),
          booking: b
        };
      });
  }

  setFormForCreate(
    userConfigs: NsWISConfigurations.IConfigurations,
    profile: NsEmpWebExpData.IEmployee,
    type: NsEmpWebExpData.IBookingRequestType,
    data?: NsEmpWebExpData.INearbyBooking
  ): NsEmpWebExpData.IBookingFormConfig {
    this.formConfiguration = {
      mode: "CREATE",
      data: undefined,
      userConfigs,
      profile,
      heading: "Create Booking",
      type: type,
      nearbyBookingDetails: data
    };
    return this.formConfiguration;
  }

  setFormForUpdate(
    userConfigs: NsWISConfigurations.IConfigurations,
    profile: NsEmpWebExpData.IEmployee,
    booking: NsEmpWebExpData.IBookingDetailDTOS,
    type?: NsEmpWebExpData.IBookingRequestType
  ): NsEmpWebExpData.IBookingFormConfig {
    this.formConfiguration = {
      mode: "EDIT",
      data: booking,
      userConfigs,
      profile,
      heading: "Edit Booking",
      type: type || booking.bookingRequestType
    };
    return this.formConfiguration;
  }

  get configurations(): NsWISConfigurations.IConfigurations {
    return this.formConfiguration.userConfigs;
  }

  castCreateBookingRequests(bookings: NsEmpWebExpData.IBookingRequest[]): void {
    return this.createBookings$.next(bookings);
  }

  castUpdateBookingRequests(bookings: NsEmpWebExpData.IBookingRequest[]): void {
    return this.updateBookings$.next(bookings);
  }

  castCancelBookingRequests(bookings: NsEmpWebExpData.IBookingRequest[]): void {
    return this.cancelBookings$.next(bookings);
  }

  setDates(start: number, end: number): void {
    this.bookingStartDate = start;
    this.bookingEndDate = end;
  }

  updateDates(): void {
    this.bookingStartDate = this.bookingEndDate + 1;
    this.bookingEndDate = this.bookingEndDate + this.interval * 24 * Hour;
  }

  genBookingRequest(): Observable<NsEmpWebExpData.IQueryResponse> {
    const query = this.apiService.genEmpDetailQuery(this.bookingStartDate, this.bookingEndDate);
    return this.apiService.getEmpProfile(query);
  }

  getShifts(start: AbstractControl, end: AbstractControl, office: NsEmpWebExpData.IOffice) {
    return this.configSvc.configurations.pipe(
      take(1),
      switchMap(configs => {
        const scheduleConfig = configs.BUSettings.features.find(item => item.id === "schedule");
        const format = configs.flattenedSettings.DATE_FORMAT;
        if (scheduleConfig?.adhocEnabled) {
          const sd = tz(start.value, format, office.timezone).format("DD/MM/YYYY");
          const ed = tz(end.value, format, office.timezone).format("DD/MM/YYYY");
          const sdd = tz(start.value, format, office.timezone).startOf("day").valueOf().toString();
          const edd = tz(start.value, format, office.timezone).startOf("day").add(1, "day").valueOf().toString();
          return combineLatest([
            this.apiService.getEmpShiftsNew(sd, ed, false, office?.address, "LOGIN"),
            this.apiService.getEmpShiftsNew(sd, ed, false, office?.address, "LOGOUT"),
            this.apiService.getEmpShiftsNew(sd, ed, true, office?.address, "LOGOUT"),
            this.getPremises(sdd, edd, office?.value)
          ]).pipe(
            map(([login, logout, nextDay, premises]) => [
              ...processShifts({ login, logout, nextDay }, sdd, office.timezone, configs.flattenedSettings.TIME_FORMAT),
              premises
            ])
          );
        }
        const sd = tz(start.value, format, office.timezone).startOf("day").valueOf().toString();
        const ed = tz(start.value, format, office.timezone).startOf("day").add(1, "day").valueOf().toString();
        return combineLatest([
          this.apiService.getEmpShifts(sd, ed, this.formConfiguration.profile, office.timezone, office).pipe(
            catchError(err => {
              this.toasts.displayMsg("Unable to fetch shifts!", 4000);
              return throwError("Shifts fetch error. Err:", err);
            })
          ),
          this.getPremises(sd, ed, office?.value)
        ]).pipe(
          map(([shifts, premises]) => {
            const [login, logout] = shifts[sd].ScheduleEdit.reduce(
              (acc, shift) => {
                if (shift.shiftType === "LOGIN") acc[0].push(shift);
                else if (shift.shiftType === "LOGOUT") acc[1].push(shift);
                return acc;
              },
              [[], []]
            ) as [NsEmpWebExpData.IShiftData[], NsEmpWebExpData.IShiftData[]];
            const nextDayLogout = shifts[ed].ScheduleEdit.filter(shift => shift.shiftType === "LOGOUT") as NsEmpWebExpData.IShiftData[];
            nextDayLogout.map(item => (item.isNextDay = true));
            return [
              ...processShifts({ login, logout, nextDay: nextDayLogout }, sd, office.timezone, configs.flattenedSettings.TIME_FORMAT),
              premises
            ];
          })
        );
      })
    );
  }

  private getPremises(sd: string, ed: string, office: string): Observable<NsEmpWebExpData.IPremise[]> {
    return this.apiService.getPremises(sd, ed, office).pipe(
      catchError(err => {
        this.toasts.displayMsg("Unable to fetch premises.", 4000);
        return of([]);
      })
    );
  }
}
